import { TransportZoneService } from 'src/app/angularJS/services/transport-zone/transport-zone.service';
import { TransportLocationService } from 'src/app/angularJS/services/transport-location/transport-location.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { UserMessagesService } from 'src/app/angularJS/services/user-messages.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { DialogService } from 'src/app/angularJS/services/dialog/dialog.service';
import { ToolbarController } from 'src/app/angularJS/layout/toolbar/toolbar.controller';

export class TransportZoneEditController {
  public static $inject = [
    'transportZoneService',
    'transportLocationService',
    '$state',
    'getTransportZoneResponse',
    'QueryBuilder',
    'LinkBuilder',
    'userService',
    'UserMessages',
    'breadcrumbService',
    'ACTToolbarService',
    '$q',
    'dialogService',
    '$stateParams',
    '$rootScope',
    'regexPatterns'
  ];
  public transportZone: InterACT.Interfaces.TransportZones.TransportZone;
  public aPageOfTransportLocationLinks: Array<InterACT.Interfaces.UsageEvents.TransportLocationLink> = [];
  public inCreateMode: boolean;
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    perPageCount: 15,
    currentPage: 1,
    totalCount: 0
  };

  public validFromTime: string = undefined;
  public validFromDate: Date = undefined;
  public validUntilTime: string = undefined;
  public validUntilDate: Date = undefined;

  private transportLocationReferenceToLocationInfoDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.ITransportLocationReference> = {};
  constructor(
    private transportZoneService: TransportZoneService,
    private transportLocationService: TransportLocationService,
    private $state: ng.ui.IStateService,
    private getTransportZoneResponse: InterACT.Interfaces.TransportZones.TransportZoneResponse,
    private queryBuilder: QueryBuilder,
    private linkBuilder: LinkBuilder,
    private userService: UserService,
    private UserMessages: UserMessagesService,
    private breadcrumbService: BreadcrumbService,
    private toolbarService: ACTToolbarService,
    private $q: ng.IQService,
    private dialogService: DialogService,
    private $stateParams: ng.ui.IStateParamsService,
    private $rootScope: InterACT.Interfaces.IActoraRootScope,
    public regexPatterns: { TIME: string, NUMBER: string }) {
    this.activate();
  }

  /**
   * Updates the validFrom field on the transport zone with the valid from date and time on the UI
   */
  public updateValidFrom = () => {
    if (this.validFromTime) {
      const validFromTime = this.validFromTime.split(':');
      this.validFromDate.setHours(parseInt(validFromTime[0], 10), parseInt(validFromTime[1], 10), 0, 0);
      this.transportZone.validFrom = <any>moment(this.validFromDate).utc().format();
    }
  }

  /**
   * Updates the validUntil field on the transport zone with the valid from date and time on the UI
   */
  public updateValidUntil = () => {
    if (this.validUntilTime) {
      const validUntilTime = this.validUntilTime.split(':');
      this.validUntilDate.setHours(parseInt(validUntilTime[0], 10), parseInt(validUntilTime[1], 10), 0, 0);
      this.transportZone.validUntil = <any>moment(this.validUntilDate).utc().format();
    }
  }

  /**
 * A method that saves the transport zone on this controller
 */
  public saveZone = () => {
    const zoneToSave = this.constructZoneObjectThatCanBeSaved();
    this.$rootScope.showSpinner = true;

    if (this.inCreateMode) {
      this.transportZoneService.create(zoneToSave).then(() => {
        this.$rootScope.showSpinner = false;
        this.UserMessages.showToast('TOAST_MESSAGE.TRANSPORT_ZONES.CREATED');
        this.$state.go('transport-zones');
      });
      return;
    }

    this.transportZoneService.update(zoneToSave).then(() => {
      this.$rootScope.showSpinner = false;
      this.UserMessages.showToast('TOAST_MESSAGE.TRANSPORT_ZONES.EDITED');
      this.$state.go('transport-zones');
    });
  };

  /**
   * A method that deletes the transport zone on this controller
   */
  public deleteTransportZone = () => {
    this.UserMessages.buildConfirmDialog(
      'DELETE_TRANSPORT_ZONE',
      'CONFIRM_DIALOG.TRANSPORT_ZONES.DELETE_CONFIRM',
      undefined,
      'CONFIRM_DIALOG.TRANSPORT_ZONES.CONFIRM',
      'CONFIRM_DIALOG.TRANSPORT_ZONES.CANCEL'
    ).then(() => {
      this.$rootScope.showSpinner = true;
      this.transportZoneService.delete(this.transportZone.id).then(() => {
        this.$rootScope.showSpinner = false;
        this.UserMessages.showToast('TOAST_MESSAGE.TRANSPORT_ZONES.DELETED');
        this.$state.go('transport-zones');
      });
    });
  };

  /**
   * A method that spawns a dialog that allows the supplied transport location link to be edited
   * @param transportLocation The transport location link to be edited
   */
  public editTransportLocationLink = (transportLocationLink: InterACT.Interfaces.UsageEvents.TransportLocationLink) => {
    const dictionaryEntryForLocationBeingEdited = this.transportLocationReferenceToLocationInfoDictionary[transportLocationLink.link];
    const transportLocationData: InterACT.Interfaces.ITransportLocationReference = {
      referenceType: dictionaryEntryForLocationBeingEdited.referenceType,
      reference: dictionaryEntryForLocationBeingEdited.reference
    };

    this.dialogService.showDialog(
      'Transport Location',
      `transport-location-edit-dialog.html`,
      { transportLocationData: transportLocationData },
      'transport-location-edit-dialog'
    ).then((editedTransportLocationReference: InterACT.Interfaces.ITransportLocationReference) => {
      this.handleEditDialogResponse(transportLocationLink,
        dictionaryEntryForLocationBeingEdited, editedTransportLocationReference);
    });
  };

  /**
 * A method that deletes the supplied transport location link from the transport zone on this controller
 * @param transportLocation The transport location link to be edited
 */
  public deleteTransportLocationLink = (transportLocationLink: InterACT.Interfaces.UsageEvents.TransportLocationLink) => {
    const indexToBeDeleted = this.transportZone.locations.indexOf(<any>transportLocationLink);
    delete this.transportLocationReferenceToLocationInfoDictionary[transportLocationLink.link];
    this.transportZone.locations.splice(indexToBeDeleted, 1);
    this.getPage(1, 15);
  };

  /**
   * A method that checks if a transport zone can be saved or not
   */
  public canSaveZone = () => {
    return this.transportZone && this.transportZone.locations && this.transportZone.locations.length > 0 && this.getNumberOfInvalidLocations() === 0;
  };

  /**
   * A method that gets a page worth of transport location links from the transport zone on this controller
   * @param pageNumber the page number to retrieve
   * @param perPage the number of location links to retrieve
   */
  public getPage = (pageNumber: number, perPage: number) => {
    if (!perPage) {
      perPage = 15;
    }

    this.paginatorData.totalCount = this.transportZone.locations.length;
    this.paginatorData.currentPage = pageNumber;

    const startIndex = (pageNumber - 1) * perPage;
    const endIndex = startIndex + (perPage - 1);
    this.aPageOfTransportLocationLinks = [];

    for (let c = startIndex; c <= endIndex; c++) {
      if (!this.transportZone.locations[c]) {
        break;
      }

      this.aPageOfTransportLocationLinks.push(<any>this.transportZone.locations[c]);
    }
  };

  /**
   * A method that spawns a dialog and allows the user to add a new transport location to the Transport Zone
   */
  public addTransportLocation = () => {
    const newTransportLocation = {
      referenceType: undefined,
      reference: undefined
    };

    this.dialogService.showDialog(
      'Transport Location',
      `transport-location-edit-dialog.html`,
      { transportLocationData: newTransportLocation },
      'transport-location-edit-dialog'
    ).then((addedTransportLocationReference: InterACT.Interfaces.ITransportLocationReference) => {
      this.handleAddDialogResponse(addedTransportLocationReference);
    });
  };

  /**
   * A method that returns the number of invalid locations that are in the transport zone
   */
  public getNumberOfInvalidLocations() {
    return this.transportZone.locations.filter((location) => {
      const locationRefInfo = this.transportLocationReferenceToLocationInfoDictionary[location.link];

      return !locationRefInfo || locationRefInfo.locationName === undefined;
    }).length;
  }

  /**
   * A method that returns the status of the transport zone. (ERRORS | UPLOADED | NOT_UPLOADED)
   */
  public getZoneStatus() {
    const numberOfInvalidLocations = this.getNumberOfInvalidLocations();
    const totalNumberOfLocationsInZone = this.transportZone.locations.length;

    if (numberOfInvalidLocations > 0 || totalNumberOfLocationsInZone === 0) {
      return 'ERRORS';
    }

    if (this.inCreateMode) {
      return 'NOT_UPLOADED';
    }

    return 'UPLOADED';
  }

  /**
 * A method that handles the event that fires when the add location dialog has completed successfully
 * @param addedTransportLocationReference the added transport location reference
 */
  private handleAddDialogResponse(
    addedTransportLocationReference: InterACT.Interfaces.ITransportLocationReference
  ) {
    const locationWithReferenceAlreadyExists = this.transportLocationReferenceToLocationInfoDictionary[addedTransportLocationReference.reference];

    if (locationWithReferenceAlreadyExists) {
      this.UserMessages.showAlert('TOAST_MESSAGE.TRANSPORT_ZONES.LOCATION_EXISTS_IN_ZONE_HEADER', 'TOAST_MESSAGE.TRANSPORT_ZONES.LOCATION_EXISTS_IN_ZONE_MESSAGE');
      return;
    }

    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);
    const query = this.queryBuilder.newParam()
      .fieldIs(`references.${addedTransportLocationReference.referenceType}`, addedTransportLocationReference.reference)
      .and()
      .openSubQuery()
      .fieldIs('scheme.link', schemeLink)
      .or()
      .fieldIs('scheme.link', '/globalScheme')
      .closeSubQuery()
      .getParam();

    this.transportLocationService.getList(1, 1, query).then((response) => {

      this.transportZone.locations.push(<any>{
        link: addedTransportLocationReference.reference,
        locationName: undefined
      });

      this.getPage(this.paginatorData.currentPage, 15);
      this.transportLocationReferenceToLocationInfoDictionary[addedTransportLocationReference.reference] = addedTransportLocationReference;

      if (response.transportLocations.length > 0) {
        const verifiedTransportLocation = response.transportLocations[0];

        addedTransportLocationReference.id = verifiedTransportLocation.id;
        addedTransportLocationReference.locationName = verifiedTransportLocation.locationName;
      } else {
        this.UserMessages.showAlert('LOCATION_NOT_FOUND', 'VALIDATION_ERRORS.INVALID_LOCATION');
      }
    });
  }

  /**
   * A method that handles the event that fires when the edit location dialog has completed successfully
   * @param transportLocationLink the transport location link that corresponds to the location that was edited
   * @param dictionaryEntryForLocationBeingEdited the dictionary entry for the transport location reference that is was edited
   * @param editedTransportLocationReference the edited transport location reference
   */
  private handleEditDialogResponse(
    transportLocationLink: InterACT.Interfaces.UsageEvents.TransportLocationLink,
    dictionaryEntryForLocationBeingEdited: InterACT.Interfaces.ITransportLocationReference,
    editedTransportLocationReference: InterACT.Interfaces.ITransportLocationReference) {
    const locationWithTheSameReference = this.transportLocationReferenceToLocationInfoDictionary[editedTransportLocationReference.reference];
    const originalTransportLocationReference = transportLocationLink.link;

    if (locationWithTheSameReference && locationWithTheSameReference !== dictionaryEntryForLocationBeingEdited) {
      this.UserMessages.showAlert('TOAST_MESSAGE.TRANSPORT_ZONES.LOCATION_EXISTS_IN_ZONE_HEADER', 'TOAST_MESSAGE.TRANSPORT_ZONES.LOCATION_EXISTS_IN_ZONE_MESSAGE');
      return;
    }

    if (editedTransportLocationReference.referenceType === dictionaryEntryForLocationBeingEdited.referenceType &&
      editedTransportLocationReference.reference === dictionaryEntryForLocationBeingEdited.reference) {
      return;
    }

    dictionaryEntryForLocationBeingEdited.referenceType = editedTransportLocationReference.referenceType;
    dictionaryEntryForLocationBeingEdited.reference = editedTransportLocationReference.reference;
    transportLocationLink.link = editedTransportLocationReference.reference;

    this.transportLocationReferenceToLocationInfoDictionary[dictionaryEntryForLocationBeingEdited.reference] =
      dictionaryEntryForLocationBeingEdited;

    //if reference on the location being edited has changed
    //delete the old dictionary entry associated with that reference
    if (editedTransportLocationReference.reference !== originalTransportLocationReference) {
      delete this.transportLocationReferenceToLocationInfoDictionary[originalTransportLocationReference];
    }

    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);
    const query = this.queryBuilder.newParam()
      .fieldIs(`references.${dictionaryEntryForLocationBeingEdited.referenceType}`, dictionaryEntryForLocationBeingEdited.reference)
      .and()
      .openSubQuery()
      .fieldIs('scheme.link', schemeLink)
      .or()
      .fieldIs('scheme.link', '/globalScheme')
      .closeSubQuery()
      .getParam();

    this.transportLocationService.getList(1, 1, query).then((response) => {
      if (response.transportLocations.length > 0) {
        const verifiedTransportLocation = response.transportLocations[0];

        dictionaryEntryForLocationBeingEdited.id = verifiedTransportLocation.id;
        dictionaryEntryForLocationBeingEdited.locationName = verifiedTransportLocation.locationName;
      } else {
        dictionaryEntryForLocationBeingEdited.id = undefined;
        dictionaryEntryForLocationBeingEdited.locationName = undefined;
        this.UserMessages.showAlert('LOCATION_NOT_FOUND', 'VALIDATION_ERRORS.INVALID_LOCATION');
      }
    });
  }

  /**
   * A method that constructs a transport zone object that can be saved/posted to the services
   */
  private constructZoneObjectThatCanBeSaved() {
    const transportZone: InterACT.Interfaces.TransportZones.TransportZone = JSON.parse(JSON.stringify(this.transportZone));

    for (let c = 0; c < transportZone.locations.length; c++) {
      const location = transportZone.locations[c];
      const locationInfo = this.transportLocationReferenceToLocationInfoDictionary[location.link];

      location.link = this.linkBuilder.getTransportLocationLink(locationInfo.id);
      delete (<any>location).locationName;
    }

    return transportZone;
  }

  /**
   * A method that gets fired on controller instantiation
   */
  private activate() {
    const dynamicBreadcrumb: InterACT.Interfaces.IBreadcrumb = {
      name: undefined,
      sref: 'x'
    };

    // this.validUntil = <any>moment(this.transportZone.validUntil).toDate();

    if (this.$stateParams.importedTransportZone) {
      dynamicBreadcrumb.name = 'PUBLISH_ZONE';
      this.initialiseController(true);
    } else {
      dynamicBreadcrumb.name = 'EDIT_ZONE';
      this.initialiseController(false);
    }

    if (this.transportZone && this.transportZone.validFrom) {
      this.validFromDate = <any>moment(this.transportZone.validFrom).toDate();
      this.validFromTime = <any>moment(this.transportZone.validFrom).format('HH:mm');
    }

    if (this.transportZone && this.transportZone.validUntil) {
      this.validUntilDate = <any>moment(this.transportZone.validUntil).toDate();
      this.validUntilTime = <any>moment(this.transportZone.validUntil).format('HH:mm');
    }

    this.breadcrumbService.setBreadcrumbs([{
      name: 'TRANSPORT_LOCATIONS',
      sref: 'x'
    }, {
      name: 'ZONES',
      sref: 'transport-zones'
    }, dynamicBreadcrumb]);
  }

  /**
   * A method that initialises this controller in the supplied mode.
   * @param inCreateMode a value that indicates if the controller should be initialised in creation or edit mode
   */
  private initialiseController(inCreateMode: boolean) {
    const toolbarTitle = inCreateMode ? 'PUBLISH_ZONE' : 'EDIT_ZONE';
    this.toolbarService.getToolbar('primary').then((toolbar: ToolbarController) => {
      toolbar.setDefaults(toolbarTitle);
    });

    this.inCreateMode = inCreateMode;
    let populateDictionary: (arg: Array<InterACT.Interfaces.ITransportLocationReference> | InterACT.Interfaces.TransportZones.TransportZone) => ng.IPromise<any>;
    let argumentToCallMethodWith: Array<InterACT.Interfaces.ITransportLocationReference> | InterACT.Interfaces.TransportZones.TransportZone;

    if (inCreateMode) {
      const importedTransportZone: InterACT.Interfaces.IImportedTransportZone = this.$stateParams.importedTransportZone;
      this.transportZone = <any>this.constructTransportZoneFromImportedTransportZone(importedTransportZone);
      populateDictionary = this.populateTransportLocationDictinaryForImportedLocations.bind(this);
      argumentToCallMethodWith = importedTransportZone.locations;
    } else {
      this.transportZone = this.getTransportZoneResponse.transportZone;
      populateDictionary = this.populateTransportLocationDictinaryForLocationsInZone.bind(this);
      argumentToCallMethodWith = this.transportZone;
    }

    this.$rootScope.showSpinner = true;
    populateDictionary(argumentToCallMethodWith).then(() => {
      const numberOfInvalidLocations = this.getNumberOfInvalidLocations();

      if (numberOfInvalidLocations > 0) {
        this.UserMessages.showErrorToast('VALIDATION_ERRORS.INVALID_LOCATIONS_IN_ZONE', undefined, undefined, 'ACTION_DETAIL.OK');
      }

      this.getPage(1, 15);
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * A method that populated the dictionary of transport location refs to ref types
   * @param transportZone the transport zone to get the locations that should be used to populate the dictionary
   */
  private populateTransportLocationDictinaryForLocationsInZone(transportZone: InterACT.Interfaces.TransportZones.TransportZone) {
    const deferred = this.$q.defer();
    const allPromises = [];

    for (let c = 0; c < transportZone.locations.length; c++) {
      const transportLocationLink = this.transportZone.locations[c];
      const promise = this.getTransportLocationReferenceForLink(<any>transportLocationLink);
      allPromises.push(promise);

      promise.then((locationInfo) => {
        if (locationInfo.reference) {
          transportLocationLink.link = locationInfo.reference;
          this.transportLocationReferenceToLocationInfoDictionary[locationInfo.reference] = locationInfo;
        }
      });
    }

    this.$q.all(allPromises).then(() => {
      deferred.resolve();
    });

    return deferred.promise;
  }

  /**
   * A method that gets the transport location link type for the supplied transport location
   * @param transportLocationLink the transport location link to get the type for
   */
  private getTransportLocationReferenceForLink(transportLocationLink: InterACT.Interfaces.UsageEvents.TransportLocationLink): ng.IPromise<InterACT.Interfaces.ITransportLocationReference> {
    const deferred = this.$q.defer<InterACT.Interfaces.ITransportLocationReference>();
    const transportLocationId = transportLocationLink.link.split('/').pop();
    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);
    const query = this.queryBuilder.newParam()
      .fieldIs('id', transportLocationId)
      .and()
      .openSubQuery()
      .fieldIs('scheme.link', schemeLink)
      .or()
      .fieldIs('scheme.link', '/globalScheme')
      .closeSubQuery()
      .getParam();

    this.transportLocationService.getList(1, 1, query).then((response) => {
      let transportLocation;
      let locationId: string = undefined;
      let referenceType: string = undefined;
      let reference: string = undefined;
      let locationName: string = undefined;

      if (response.transportLocations && response.transportLocations.length > 0) {
        transportLocation = response.transportLocations[0];
        locationId = transportLocation.id;
        locationName = transportLocation.locationName;

        if ((<any>transportLocation.references).atcoCode) {
          reference = (<any>transportLocation.references).atcoCode;
          referenceType = 'atcoCode';
        } else if ((<any>transportLocation.references).naptanCode) {
          reference = (<any>transportLocation.references).naptanCode;
          referenceType = 'naptanCode';
        } else if ((<any>transportLocation.references).nlcCode) {
          reference = (<any>transportLocation.references).nlcCode;
          referenceType = 'nlcCode';
        } else if ((<any>transportLocation.references).lightRailCode) {
          reference = (<any>transportLocation.references).lightRailCode;
          referenceType = 'lightRailCode';
        } else {
          reference = transportLocation.id;
          referenceType = 'id';
        }
      }

      deferred.resolve({
        reference: reference,
        referenceType: referenceType,
        locationName: locationName,
        id: locationId
      });
    });

    return deferred.promise;
  }

  /**
   * A method that contructs a transport zone from an imported version of a transport zone
   * @param importedTransportZone the imported transport zone to use to construct a real transport zone
   */
  private constructTransportZoneFromImportedTransportZone(importedTransportZone: InterACT.Interfaces.IImportedTransportZone) {
    return {
      scheme: {
        abbreviation: this.userService.selectedScheme.reference,
        link: this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id)
      },
      name: importedTransportZone.name,
      validFrom: importedTransportZone.validFrom,
      validUntil: importedTransportZone.validUntil,
      locations: []
    };
  }

  /**
   * A method that populates the transport location dictionary for the location references supplied
   * @param importedLocations The imported transport location references to use to populate the transport location dictionary
   */
  private populateTransportLocationDictinaryForImportedLocations(importedLocations: Array<InterACT.Interfaces.ITransportLocationReference>) {
    const allPromises = [];

    if (!importedLocations) {
      return;
    }

    const referencesAlreadyDealtWith = [];

    for (let c = 0; c < importedLocations.length; c++) {
      if (referencesAlreadyDealtWith.indexOf(importedLocations[c].reference) !== -1) {
        this.UserMessages.showToast('TOAST_MESSAGE.TRANSPORT_ZONES.DUPLICATE_LOCATIONS_ON_IMPORT_DETECTED');
        continue;
      }

      referencesAlreadyDealtWith.push(importedLocations[c].reference);
      allPromises.push(this.populateTransportLocationDictinaryForImportedLocation(importedLocations[c]));
    }

    return this.$q.all(allPromises);
  }

  /**
 * A method that populates the transport location  dictionary using the supplied transport location reference
 * @param importedLocation The imported transport location to use to populate the dictionary with
 */
  private populateTransportLocationDictinaryForImportedLocation(importedLocation: InterACT.Interfaces.ITransportLocationReference) {
    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);
    const query = this.queryBuilder.newParam()
      .fieldIs(`references.${importedLocation.referenceType}`, importedLocation.reference)
      .and()
      .openSubQuery()
      .fieldIs('scheme.link', schemeLink)
      .or()
      .fieldIs('scheme.link', '/globalScheme')
      .closeSubQuery()
      .getParam();

    return this.transportLocationService.getList(1, 1, query).then((response) => {
      let locationName = undefined;
      let transportLocation = undefined;
      let locationId = undefined;
      const referenceType = importedLocation.referenceType;
      const reference = importedLocation.reference;

      if (response.transportLocations && response.transportLocations.length > 0) {
        transportLocation = response.transportLocations[0];
        locationId = transportLocation.id;
        locationName = transportLocation.locationName;
      }

      const transportLocationInfo = {
        reference: reference,
        referenceType: referenceType,
        locationName: locationName,
        id: locationId
      };

      this.transportZone.locations.push(<any>{
        locationName: undefined,
        link: importedLocation.reference
      });

      if (!this.transportLocationReferenceToLocationInfoDictionary[transportLocationInfo.reference]) {
        this.transportLocationReferenceToLocationInfoDictionary[transportLocationInfo.reference] = transportLocationInfo;
      }
    });
  }
}